import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, UserRoundPlus } from 'lucide-react';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { DriverGroupPill } from '@/modules/driver-groups/components/DriverGroupPill';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

interface Props {
  groups: DriverGroupDto[];
  activeGroupId: string | null;
  allDriversCount: number;
  onSelect: (groupId: string | null) => void;
  onCreate: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function DriverGroupPills({
  groups,
  activeGroupId,
  allDriversCount,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();

  const handleSelectAll = (): void => {
    onSelect(null);
  };

  const handleSelectGroup = (groupId: string): (() => void) => {
    return (): void => {
      onSelect(groupId);
    };
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <DriverGroupPill
        label={t('driverGroups.allDrivers')}
        count={allDriversCount}
        isActive={activeGroupId === null}
        onSelect={handleSelectAll}
      />

      {groups.map((group: DriverGroupDto) => (
        <DriverGroupPill
          key={group.id}
          label={group.name}
          count={group.memberIds.length}
          isActive={group.id === activeGroupId}
          onSelect={handleSelectGroup(group.id)}
        >
          {group.id === activeGroupId && (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('driverGroups.editGroupTitle')}
                title={t('driverGroups.editGroupTitle')}
                className="flex items-center justify-center w-5.5 h-5.5 mr-1 rounded-full bg-white/20 hover:bg-white/30"
              >
                <Pencil size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onSelect={onEdit}>
                  <UserRoundPlus size={15} className="mr-2 text-muted-foreground" />
                  {t('driverGroups.manageMembers')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onDelete} className="text-destructive">
                  <Trash2 size={15} className="mr-2" />
                  {t('driverGroups.deleteGroup')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </DriverGroupPill>
      ))}

      <button
        type="button"
        onClick={onCreate}
        aria-label={t('driverGroups.createGroup')}
        title={t('driverGroups.createGroup')}
        className="flex items-center justify-center w-7 h-7 rounded-full border border-dashed border-violet-300 bg-card text-violet-600 hover:bg-violet-50 hover:border-violet-600"
      >
        <Plus size={15} />
      </button>
    </div>
  );
}
